import React, { useEffect, useState } from 'react';
import { useGlobalVariablesContext } from '../../context/GlobalVariablesContext';
import TileWMS from 'ol/source/TileWMS.js';
import TileLayer from 'ol/layer/Tile';
import './LegendsComponent.css';

const LegendsComponent = () => {
  const { mapRef } = useGlobalVariablesContext();
  const map = mapRef?.current;
  const [legends, setLegends] = useState([]);
  const [layerVisibility, setLayerVisibility] = useState({});
  const { aLayersExternas } = useGlobalVariablesContext();

  // Función para obtener todas las capas visibles
  const getVisibleLayers = (map) => {
    if (!map) return [];
    
    const allLayers = map.getLayers().getArray();
   // console.log('🔍 Total de capas en el mapa:', allLayers.length);
    
    allLayers.forEach((layer, index) => {
      // console.log(`🔍 Capa ${index}:`, {
      //   name: layer.get('name'),
      //   title: layer.get('title'),
      //   type: layer.get('type'),
      //   visible: layer.getVisible(),
      //   isTileLayer: layer instanceof TileLayer,
      //   hasSource: !!layer.getSource(),
      //   sourceType: layer.getSource()?.constructor?.name,
      //   isVector: layer.getSource && (layer.getSource().getFeatures || layer.getSource().getUrl),
      //   isWMS: layer instanceof TileLayer && layer.getSource() instanceof TileWMS
      // });
    });

    const visibleLayers = allLayers
      .map((layer) => {
        const layerName = layer.get('name') || 'Sin nombre';
        const layerTitle = layer.get('title') || 'Sin título';
        const isVisible = layer.getVisible();
        const layerType = layer.get('type');
        
        // Procesar capas overlay (no mapas base)
        if (layerType === 'overlay') {
          // console.log(`🔍 Procesando capa overlay: ${layerName} (visible: ${isVisible})`);
          
          // Para capas WMS
          if (layer instanceof TileLayer && layer.getSource() instanceof TileWMS) {
            //console.log(`🔍 Capa WMS detectada: ${layerName}`);
            const resolution = map.getView().getResolution();
            return {
              title: layerTitle,
              legendUrl: layer.getSource().getLegendUrl(resolution),
              visible: isVisible,
              name: layerName,
              type: 'wms'
            };
          } 
          // Para capas vectoriales (GeoJSON)
          else if (layer.getSource && (layer.getSource().getFeatures || layer.getSource().getUrl)) {
            console.log(`🔍 Capa vectorial detectada: ${layerName}`);
            const source = layer.getSource();
            const features = source.getFeatures ? source.getFeatures() : [];
            return {
              title: layerTitle,
              legendUrl: null,
              visible: isVisible,
              name: layerName,
              type: 'vector',
              featureCount: features.length,
              iconColor: layer.get('iconColor') || '#007bff',
              sourceType: source.constructor.name
            };
          } 
          // Detección por nombre para capas que no se detectan automáticamente
          else if (layerName.includes('arco') || layerName.includes('Catastro')) {
            console.log(`🔍 Detectando como capa especial por nombre: ${layerName}`);
            const source = layer.getSource();
            const features = source && source.getFeatures ? source.getFeatures() : [];
            
            // Determinar si es WMS o vectorial por el nombre
            const isWMS = layerName.includes('Catastro') || layerName.includes('WMS');
            const isVector = layerName.includes('arco') || layerName.includes('invasoras');
            
            return {
              title: layerTitle,
              legendUrl: isWMS && layer instanceof TileLayer && layer.getSource() instanceof TileWMS 
                ? layer.getSource().getLegendUrl(map.getView().getResolution()) 
                : null,
              visible: isVisible,
              name: layerName,
              type: isWMS ? 'wms' : 'vector',
              featureCount: features.length,
              iconColor: layer.get('iconColor') || '#007bff',
              sourceType: source ? source.constructor.name : 'detected'
            };
          }
        }
        return null;
      })
      .filter((layer) => layer !== null);

    // console.log('🔍 Capas procesadas:', visibleLayers);
    return visibleLayers;
  };

  // Efecto principal para detectar capas
  useEffect(() => {
    if (!map) {
      // console.log('🔍 LegendsComponent: No hay mapa disponible');
      return;
    }

    // console.log('🔍 LegendsComponent: Mapa disponible, iniciando detección de capas...');

    // Agregar un pequeño delay para asegurar que las capas estén cargadas
    const timeoutId = setTimeout(() => {
      const layersToShow = getVisibleLayers(map);
      // console.log('🔍 Capas finales para mostrar:', layersToShow);
      setLegends(layersToShow);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [map, aLayersExternas]);

  // Efecto para escuchar cambios de visibilidad
  useEffect(() => {
    if (!map) return;

    const handleVisibilityChange = () => {
      console.log('🔍 Cambio de visibilidad detectado, actualizando leyendas...');
      const layersToShow = getVisibleLayers(map);
      setLegends(layersToShow);
    };

    // Escuchar cambios en todas las capas
    const layers = map.getLayers().getArray();
    layers.forEach(layer => {
      layer.on('change:visible', handleVisibilityChange);
    });

    return () => {
      layers.forEach(layer => {
        layer.un('change:visible', handleVisibilityChange);
      });
    };
  }, [map]);

  const renderLegend = (legend, index) => {
    if (!legend.visible) return null;

    return (
      <div key={index} className="legend-item">
        <div className="legend-header">
          <h6 className="legend-title">{legend.title}</h6>
          <small className="text-muted">
            {legend.type === 'vector' ? 'Capa vectorial' : 'Capa WMS'}
          </small>
        </div>
        <div className="legend-content">
          {legend.type === 'wms' ? (
            <div className="legend-image-container">
              {legend.legendUrl ? (
                <>
                  <img 
                    src={legend.legendUrl} 
                    alt={`Leyenda ${legend.title}`}
                    className="legend-image"
                    onLoad={() => console.log(`🔍 Leyenda cargada: ${legend.title}`)}
                    onError={(e) => {
                      console.log(`🔍 Error cargando leyenda: ${legend.title}`, e);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="legend-error" style={{ display: 'none' }}>
                    <i className="bi bi-exclamation-triangle text-warning"></i>
                    <small className="text-muted">No se pudo cargar la leyenda</small>
                  </div>
                </>
              ) : (
                <div className="legend-no-url">
                  <i className="bi bi-info-circle text-info"></i>
                  <small className="text-muted">Leyenda no disponible para esta capa WMS</small>
                </div>
              )}
            </div>
          ) : legend.type === 'vector' ? (
            <div className="legend-vector-info">
              <div className="legend-vector-icon" style={{ backgroundColor: legend.iconColor }}>
                <i className="bi bi-geo-alt-fill text-white"></i>
              </div>
              <div className="legend-vector-details">
                <div className="legend-vector-count">
                  <i className="bi bi-collection me-1"></i>
                  <strong>{legend.featureCount}</strong> elementos
                </div>
                <div className="legend-vector-type">
                  <i className="bi bi-layers me-1"></i>
                  Capa vectorial
                </div>
              </div>
            </div>
          ) : (
            <div className="legend-no-info">
              <i className="bi bi-info-circle text-muted"></i>
              <small className="text-muted">Sin información de leyenda disponible</small>
            </div>
          )}
        </div>
      </div>
    );
  };

  const visibleLegends = legends.filter(legend => legend.visible);

  // Función para forzar actualización (debug)
  const forceUpdate = () => {
    console.log('🔍 Forzando actualización de leyendas...');
    if (map) {
      const layersToShow = getVisibleLayers(map);
      setLegends(layersToShow);
    }
  };

  return (
    <div className="legends-component">
      <div className="legends-header">
        <h5 className="legends-title">
          <i className="bi bi-list-ul me-2"></i>
          Leyendas de Capas Visibles
        </h5>
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {visibleLegends.length} capa{visibleLegends.length !== 1 ? 's' : ''} visible{visibleLegends.length !== 1 ? 's' : ''}
          </small>
          <button 
            className="btn btn-sm btn-outline-primary"
            onClick={forceUpdate}
            title="Forzar actualización"
          >
            <i className="bi bi-arrow-clockwise"></i>
          </button>
        </div>
      </div>

      <div className="legends-content">
        {visibleLegends.length > 0 ? (
          <div className="legends-list">
            {legends.map((legend, index) => renderLegend(legend, index))}
          </div>
        ) : (
          <div className="legends-empty">
            <div className="text-center py-4">
              <i className="bi bi-eye-slash text-muted" style={{ fontSize: '2rem' }}></i>
              <p className="text-muted mt-2 mb-0">No hay capas visibles</p>
              <small className="text-muted">Activa algunas capas para ver sus leyendas</small>
            </div>
          </div>
        )}
      </div>

      {/* Información adicional */}
      {visibleLegends.length > 0 && (
        <div className="legends-footer">
          <div className="alert alert-info alert-sm mb-0">
            <i className="bi bi-info-circle me-1"></i>
            <small>
              Las leyendas se actualizan automáticamente cuando cambias la visibilidad de las capas.
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegendsComponent;
